/*
 * The 5th Note fragrance search.
 * Searches the fragrance library (assets/fragrance-library.js, built from the DNA spreadsheet)
 * by name, brand, notes, smells, moods, occasions, gender and category, and shows matching
 * fragrances with their DNA. "Smells like X" queries rank by DNA similarity to X.
 * Fragrances you stock are linked to their product page.
 */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, '').replace(/[^a-z0-9& ]+/g, ' ').replace(/\s+/g, ' ').trim();

  /* ---------- load the library on first use ---------- */
  let libPromise = null;
  const loadLibrary = () => {
    if (window.FifthLibrary) return Promise.resolve(prepare(window.FifthLibrary));
    if (libPromise) return libPromise;
    libPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = window.fifthLibraryUrl;
      s.onload = () => resolve(prepare(window.FifthLibrary));
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return libPromise;
  };
  let prepared = null;
  function prepare(lib) {
    if (prepared) return prepared;
    const D = lib.dims.map((d) => d.toLowerCase());
    const items = lib.items.map(([brand, name, category, gender, style, bestFor, dna, notes, x], i) => ({
      id: i, brand, name, category, gender, style, bestFor, dna, notes,
      of: x ? x.of : '', ofs: x ? (x.ofs || [x.of]) : [], from: x ? x.from : null, url: x ? x.url : '',
      nName: norm(name), nBrand: norm(brand), nFull: norm(brand + ' ' + name),
      nMeta: norm([category, gender, style, bestFor, notes, x ? 'dupe ' + x.of : ''].join(' ')),
      words: norm(brand + ' ' + name + (x ? ' ' + x.of : '')).split(' '),
      nNotes: ' ' + String(notes || '').split(',').map((n) => norm(n)).filter(Boolean).join(' | ') + ' '
    }));
    // every note word and note phrase in the library, with how many fragrances use it
    const noteVocab = new Map();
    items.forEach((it) => {
      const seen = new Set();
      it.nNotes.split('|').map((n) => n.trim()).filter(Boolean).forEach((n) => { seen.add(n); n.split(' ').forEach((w) => { if (w.length > 2) seen.add(w); }); });
      seen.forEach((k) => noteVocab.set(k, (noteVocab.get(k) || 0) + 1));
    });
    // SA dupes grouped under the fragrance they copy
    const byFull = {};
    items.forEach((it) => { if (!it.of) byFull[it.brand + ' ' + it.name] = it; });
    items.forEach((it) => { it.ofs.forEach((o) => { if (byFull[o]) (byFull[o].dupes = byFull[o].dupes || []).push(it); }); });
    items.forEach((it) => { if (it.dupes) it.dupes.sort((a, b) => (a.from || 1e9) - (b.from || 1e9)); });
    return (prepared = { D, items, noteVocab });
  }

  /* ---------- what words mean ---------- */
  // smell words and notes -> DNA characteristics (weights)
  const SMELL = {
    fresh: { freshness: 1 }, clean: { freshness: 1, aquatic: 0.4 }, crisp: { freshness: 1 }, light: { freshness: 0.6, intensity: -0.6 },
    sweet: { sweetness: 1 }, sugary: { sweetness: 1 }, gourmand: { sweetness: 1, creamy: 0.5 }, vanilla: { sweetness: 0.8, creamy: 0.7, warmth: 0.3 },
    caramel: { sweetness: 1 }, honey: { sweetness: 0.9, warmth: 0.3 }, chocolate: { sweetness: 0.9, creamy: 0.4 }, coffee: { sweetness: 0.5, smoky: 0.3, warmth: 0.4 },
    tonka: { sweetness: 0.6, creamy: 0.8 }, praline: { sweetness: 1 }, candy: { sweetness: 1, fruity: 0.4 }, almond: { sweetness: 0.6, creamy: 0.6 },
    woody: { woody: 1 }, wood: { woody: 1 }, woods: { woody: 1 }, cedar: { woody: 1 }, sandalwood: { woody: 0.8, creamy: 0.7 }, vetiver: { woody: 0.9, green: 0.4 },
    patchouli: { woody: 0.7, green: 0.3 }, oud: { woody: 0.8, smoky: 0.4, warmth: 0.5 }, agarwood: { woody: 0.8, smoky: 0.4 },
    spicy: { spicy: 1 }, spice: { spicy: 1 }, pepper: { spicy: 1 }, cinnamon: { spicy: 1, warmth: 0.4 }, cardamom: { spicy: 0.9 }, saffron: { spicy: 0.8, warmth: 0.4 },
    nutmeg: { spicy: 0.9 }, ginger: { spicy: 0.8, freshness: 0.3 }, clove: { spicy: 1 },
    smoky: { smoky: 1 }, smoke: { smoky: 1 }, incense: { smoky: 1 }, tobacco: { smoky: 0.8, sweetness: 0.3, warmth: 0.4 }, leather: { smoky: 0.6, woody: 0.4, intensity: 0.4 },
    creamy: { creamy: 1 }, milky: { creamy: 1 }, smooth: { creamy: 0.8 }, coconut: { creamy: 1, sweetness: 0.4 }, musk: { creamy: 0.5, freshness: 0.3 }, musky: { creamy: 0.5, freshness: 0.3 },
    citrus: { citrus: 1 }, citrusy: { citrus: 1 }, lemon: { citrus: 1, freshness: 0.5 }, bergamot: { citrus: 1, freshness: 0.4 }, orange: { citrus: 0.9 }, grapefruit: { citrus: 1, freshness: 0.4 },
    mandarin: { citrus: 0.9 }, lime: { citrus: 1 }, neroli: { citrus: 0.5, floral: 0.6 },
    fruity: { fruity: 1 }, fruit: { fruity: 1 }, apple: { fruity: 1 }, pineapple: { fruity: 1 }, berry: { fruity: 1 }, berries: { fruity: 1 }, raspberry: { fruity: 1 },
    peach: { fruity: 1 }, pear: { fruity: 1 }, plum: { fruity: 0.9 }, cherry: { fruity: 1, sweetness: 0.5 }, mango: { fruity: 1 }, blackcurrant: { fruity: 1 },
    floral: { floral: 1 }, flowery: { floral: 1 }, flower: { floral: 1 }, rose: { floral: 1 }, jasmine: { floral: 1 }, lavender: { floral: 0.7, green: 0.5 }, iris: { floral: 0.8 },
    tuberose: { floral: 1 }, violet: { floral: 0.8 }, powdery: { floral: 0.6, creamy: 0.5 },
    aquatic: { aquatic: 1 }, marine: { aquatic: 1 }, ocean: { aquatic: 1 }, sea: { aquatic: 1 }, water: { aquatic: 0.8 }, ozonic: { aquatic: 0.8, freshness: 0.5 }, salty: { aquatic: 0.8 }, blue: { aquatic: 0.6, freshness: 0.6 },
    green: { green: 1 }, herbal: { green: 1 }, mint: { green: 0.7, freshness: 0.7 }, grass: { green: 1 }, tea: { green: 0.8, freshness: 0.4 }, sage: { green: 0.8 }, basil: { green: 0.8 }, earthy: { green: 0.5, woody: 0.5 },
    strong: { projection: 1, intensity: 0.6 }, beast: { projection: 1, longevity: 0.6, intensity: 0.8 }, loud: { projection: 1 }, powerful: { projection: 0.8, intensity: 0.8 }, compliment: { projection: 0.6 }, compliments: { projection: 0.6 },
    lasting: { longevity: 1 }, longlasting: { longevity: 1 }, longevity: { longevity: 1 },
    warm: { warmth: 1 }, cozy: { warmth: 1, sweetness: 0.3 }, cosy: { warmth: 1, sweetness: 0.3 }, amber: { warmth: 0.9, sweetness: 0.4 }, resin: { warmth: 0.8 },
    cool: { freshness: 0.8, warmth: -0.6 }, cold: { freshness: 0.6, warmth: -0.5 },
    versatile: { versatility: 1 }, intense: { intensity: 1 }, bold: { intensity: 0.9, projection: 0.4 }, subtle: { projection: -0.8, intensity: -0.6 }, soft: { projection: -0.5, creamy: 0.4 },
    sexy: { sweetness: 0.4, warmth: 0.5, creamy: 0.3 }, seductive: { sweetness: 0.4, warmth: 0.5 }
  };
  // occasion / mood words -> text that appears in "Best For" or "Primary Style"
  const CONTEXT = {
    summer: 'summer', day: 'day', daytime: 'day', everyday: 'everyday', office: 'everyday', work: 'everyday', casual: 'casual', school: 'everyday', gym: 'summer',
    evening: 'evening', night: 'evening', nights: 'evening', club: 'evening', party: 'evening', date: 'date', dates: 'date', romantic: 'date',
    winter: 'cold weather', events: 'events', event: 'events', wedding: 'events', formal: 'events', statement: 'statement'
  };
  const GENDER = { men: "men's", mens: "men's", man: "men's", male: "men's", him: "men's", masculine: "men's", guys: "men's",
    women: "women's", womens: "women's", woman: "women's", female: "women's", her: "women's", ladies: "women's", feminine: "women's", unisex: 'unisex', shared: 'unisex' };
  const CATEGORY = { arabian: 'arabian', arabic: 'arabian', arab: 'arabian', middle: 'arabian', dubai: 'arabian', niche: 'niche', designer: 'designer',
    dupe: 'dupe', dupes: 'dupe', clone: 'dupe', clones: 'dupe', inspired: 'dupe', cheap: 'budget', affordable: 'budget', budget: 'budget' };
  const STOP = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'with', 'of', 'to', 'in', 'on', 'that', 'is', 'my', 'me', 'i', 'want', 'something', 'fragrance', 'fragrances',
    'perfume', 'perfumes', 'cologne', 'colognes', 'scent', 'scents', 'smell', 'smells', 'smelling', 'like', 'similar', 'good', 'best', 'nice', 'very', 'really', 'not', 'too', 'eau', 'de', 'parfum', 'toilette']);

  const lev1 = (a, b) => { // true if edit distance <= 1
    if (a === b) return true;
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++edits > 1) return false;
      if (a.length > b.length) i++; else if (b.length > a.length) j++; else { i++; j++; }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
  };

  /* ---------- search ---------- */
  function search(lib, raw) {
    const q = norm(raw).replace(/long lasting/g, 'longlasting');
    // "All" button: every fragrance, A-Z by house then name
    if (/^(all|show all|everything|all fragrances|all colognes)$/.test(q)) {
      const all = lib.items.slice().sort((a, b) => a.nBrand.localeCompare(b.nBrand) || a.nName.localeCompare(b.nName));
      return { hits: all.map((it) => ({ it, s: 0 })), all: true, similarTo: null, understood: {} };
    }
    if (!q) return { hits: [], similarTo: null };
    const tokens = q.split(' ');
    const D = lib.D;
    const want = {}; const ctx = []; let gender = null; const cats = []; const avoid = {};
    const nameTokens = []; const wantWords = []; const avoidWords = [];
    let negate = false;
    tokens.forEach((t) => {
      if (t === 'no' || t === 'not' || t === 'without' || t === 'less') { negate = true; return; }
      if (SMELL[t]) {
        (negate ? avoidWords : wantWords).push(t);
        Object.entries(SMELL[t]).forEach(([d, w]) => { (negate ? avoid : want)[d] = ((negate ? avoid : want)[d] || 0) + w; });
        negate = false;
      } else if (CONTEXT[t]) ctx.push(CONTEXT[t]);
      else if (GENDER[t]) gender = GENDER[t];
      else if (CATEGORY[t]) cats.push(CATEGORY[t]);
      if (!STOP.has(t)) nameTokens.push(t);
      if (!SMELL[t]) negate = false;
    });
    const smellLike = /\b(like|similar|smells like|dupe of|clone of|alternative)\b/.test(q);

    // 1. name and brand matches
    const nameScore = (it) => {
      if (!nameTokens.length) return 0;
      if (it.nFull === q || it.nName === q) return 100;
      let hit = 0, strong = 0;
      nameTokens.forEach((t) => {
        if (it.words.includes(t)) { hit++; strong++; }
        else if (t.length >= 3 && it.words.some((w) => w.startsWith(t))) hit += 0.8;
        else if (t.length >= 4 && it.words.some((w) => w.length >= 4 && lev1(w, t))) hit += 0.6;
      });
      if (!hit) return 0;
      const share = hit / nameTokens.length;
      return (it.nFull.includes(q) ? 30 : 0) + share * 40 + strong * 5;
    };
    const wantsDupe = cats.includes('dupe') || /\b(dupes?|clones?|inspired|alternatives?|cheaper)\b/.test(q);
    // dupes rank below the original on a plain name search
    const named = lib.items.map((it) => ({ it, s: nameScore(it) * (it.of && !wantsDupe ? 0.85 : 1) })).filter((x) => x.s >= 20).sort((a, b) => b.s - a.s);
    // "sauvage dupe": the original, then every SA dupe of it, cheapest first
    if (wantsDupe) {
      const orig = named.find((x) => !x.it.of && x.it.dupes && x.s >= 20);
      if (orig) return { hits: [{ it: orig.it, s: 999, exact: true }].concat(orig.it.dupes.map((it) => ({ it, s: 0 }))), dupesOf: orig.it, similarTo: null, understood: {} };
    }
    const nameHits = named;

    // brand search ("lattafa", "tom ford"): list that house
    const brandQ = nameTokens.join(' ');
    const brandItems = brandQ.length >= 3 ? lib.items.filter((it) => it.nBrand === brandQ || (brandQ.length >= 4 && lev1(it.nBrand, brandQ))) : [];
    if (brandItems.length && !smellLike) {
      const hasF = Object.keys(want).length || ctx.length || gender || cats.length;
      return { hits: brandItems.map((it) => ({ it, s: 0 })).slice(0, 60), similarTo: null, brand: brandItems[0].brand, understood: { want: [], avoid: [], ctx: [], cats: [] }, filtered: hasF };
    }

    // 2. note search ("vanilla", "oud rose", "pink pepper"): fragrances whose notes contain it
    const NOT_NOTES = /^(fresh|clean|crisp|light|sweet|sugary|gourmand|woody|wood|woods|spicy|spice|smoky|smoke|creamy|milky|smooth|musky|citrusy|floral|flowery|fruity|green|aquatic|marine|powdery|warm|strong|soft|notes?|accords?|dark|white|black|red|blue|pink|sea|absolute|oil|extract|essence|leaf|leaves|flower|flowers|wild|bitter|dry|summer|winter|night|day|men|women|unisex|dupe|dupes)$/;
    const vocab = lib.noteVocab || new Map();
    const content = nameTokens.filter((t) => !CONTEXT[t] && !GENDER[t] && !CATEGORY[t]);
    let noteTerms = [];
    const phrase = content.join(' ');
    if (content.length > 1 && (vocab.get(phrase) || 0) >= 2) noteTerms = [phrase];
    else if (content.length && content.every((t) => (vocab.get(t) || 0) >= 3 && !NOT_NOTES.test(t))) noteTerms = content;
    else if (content.length === 2) {
      // two-word notes inside a longer query, e.g. "pink pepper and oud"
      for (let i = 0; i < content.length - 1; i++) { const pr = content[i] + ' ' + content[i + 1]; if ((vocab.get(pr) || 0) >= 2) noteTerms = [pr]; }
    }
    const exactName = lib.items.some((it) => it.nFull === q);
    if (noteTerms.length && !smellLike && !exactName) {
      const has = (it, t) => it.nNotes.includes(' ' + t + ' ');
      const scored = lib.items.filter((it) => !it.of).map((it) => {
        const hitN = noteTerms.filter((t) => has(it, t)).length;
        if (!hitN) return null;
        let s = hitN * 10;
        // a note in the first half of the list (top/heart) counts a little more than a faint base note
        noteTerms.forEach((t) => { const i = it.nNotes.indexOf(' ' + t + ' '); if (i >= 0) s += 2 * (1 - i / it.nNotes.length); });
        if (noteTerms.some((t) => it.nFull.includes(t))) s += 1.5;
        if (gender || ctx.length || cats.length || Object.keys(avoid).length) s += smellScore(it) * 0.5;
        return { it, s, all: hitN === noteTerms.length };
      }).filter(Boolean);
      // fragrances we have no note list for: include them when their scent profile is strongly that note
      if (noteTerms.every((t) => SMELL[t])) {
        lib.items.forEach((it) => {
          if (it.of || it.nNotes.trim()) return;
          const v = Math.min(...noteTerms.map((t) => { const e = Object.entries(SMELL[t]).sort((a, b) => b[1] - a[1])[0]; return it.dna[D.indexOf(e[0])] / 10; }));
          if (v >= 0.7) scored.push({ it, s: 8 * v, all: true });
        });
      }
      const allMatch = scored.filter((x) => x.all);
      const pool = (allMatch.length ? allMatch : scored).sort((a, b) => b.s - a.s);
      if (pool.length) return { hits: pool.slice(0, 60), total: pool.length, noteQ: noteTerms, similarTo: null, understood: { want: [], avoid: [], ctx, gender, cats } };
    }

    // 3. "smells like X": rank everything by DNA similarity to the best name match
    let similarTo = null;
    const top = nameHits[0];
    const queryIsMostlyName = top && top.s >= 45 && Object.keys(want).length === 0;
    if (top && (smellLike || queryIsMostlyName)) similarTo = top.it;

    // 4. smell / occasion / gender / category scoring
    const hasFilters = Object.keys(want).length || Object.keys(avoid).length || ctx.length || gender || cats.length;
    function smellScore(it) {
      let s = 0;
      Object.entries(want).forEach(([d, w]) => { const v = it.dna[D.indexOf(d)] / 10; s += w >= 0 ? w * v * v * 3 : -w * (1 - v) * 2; });
      Object.entries(avoid).forEach(([d, w]) => { const v = it.dna[D.indexOf(d)] / 10; s -= w * v * v * 8; });
      const meta = it.nMeta;
      tokens.forEach((t) => {
        if (t.length < 3 || STOP.has(t)) return;
        const named = it.nFull.includes(t) || meta.includes(t);
        if (named) s += SMELL[t] && !/^(fresh|sweet|warm|strong|light|soft|clean|cool|cold|bold|intense)$/.test(t) ? 3 : 1.2;
      });
      ctx.forEach((c) => { if (c === 'budget') return; s += meta.includes(c) ? 1.5 : -0.3; });
      if (gender) s += it.gender.toLowerCase() === gender ? 1 : it.gender.toLowerCase() === 'unisex' ? 0.4 : -3;
      cats.forEach((c) => {
        const cat = it.category.toLowerCase();
        if (c === 'budget') s += /arabian|dupe/.test(cat) ? 1.5 : -1;
        else s += cat.includes(c) ? 2 : -2;
      });
      return s;
    };

    let hits = [];
    if (similarTo) {
      const base = similarTo.dna;
      hits = lib.items.filter((it) => it !== similarTo).map((it) => {
        let d2 = 0;
        it.dna.forEach((v, i) => { d2 += (v - base[i]) * (v - base[i]); });
        const sim = Math.exp(-d2 / (2 * 16 * 2.2 * 2.2));
        return { it, s: sim * 10 + (hasFilters ? smellScore(it) : 0), sim };
      }).sort((a, b) => b.s - a.s).slice(0, 11);
      hits.unshift({ it: similarTo, s: 999, exact: true });
      // other name matches (e.g. EDT vs EDP) right after the exact one
      const variants = nameHits.filter((n) => n.s >= 40).slice(1, 8).map((n) => n.it);
      hits = hits.filter((h) => !variants.includes(h.it));
      hits.splice(1, 0, ...variants.map((it) => ({ it, s: 500, exact: true })));
    } else if (hasFilters) {
      hits = lib.items.map((it) => ({ it, s: smellScore(it) + (nameScore(it) / 20) })).filter((x) => x.s > 0.5).sort((a, b) => b.s - a.s).slice(0, 24);
    } else {
      hits = nameHits.slice(0, 24).map((x) => ({ it: x.it, s: x.s, exact: x.s >= 45 }));
    }
    return { hits, similarTo, understood: { want: wantWords, avoid: avoidWords, ctx, gender, cats } };
  }

  /* ---------- stocked products (link fragrances you sell) ---------- */
  let stockPromise = null;
  const loadStock = () => {
    if (stockPromise) return stockPromise;
    stockPromise = (window.FifthDNA ? window.FifthDNA.loadCatalogue() : Promise.reject()).then((cat) => {
      const idx = cat.list.map((p) => ({ p, n: norm(p.vendor + ' ' + p.title), t: norm(p.title) }));
      const CONC = /\b(edp|edt|parfum|extrait|elixir|intense|cologne)\b/g;
      const conc = (str) => (str.match(CONC) || []).filter((c) => c !== 'parfum' || !/eau de parfum/.test(str));
      return (it) => {
        const name = it.nName.replace(CONC, '').replace(/\s+/g, ' ').trim();
        const brand = it.nBrand.split(' ')[0];
        const want = conc(it.nName);
        return (idx.find((x) => {
          if (!x.n.includes(brand) || !x.t.includes(name)) return false;
          const have = conc(x.t.replace(/eau de toilette/, 'edt').replace(/eau de parfum/, 'edp'));
          return want.every((c) => have.includes(c)) && have.every((c) => want.includes(c) || (want.length && /^(edp|edt|intense)$/.test(c)) || (!want.length && /^(edp|edt)$/.test(c)));
        }) || {}).p || null;
      };
    }).catch(() => () => null);
    return stockPromise;
  };

  /* ---------- rendering ---------- */
  const money = (n) => (window.fifthNote ? window.fifthNote.money(Math.round(n * 100)) : 'R' + n).replace(/[.,]00(?!\d)/, '');
  const topDims = (lib, it, n) => lib.D.slice(0, 11).map((d, i) => ({ d, v: it.dna[i] })).sort((a, b) => b.v - a.v).slice(0, n);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  function card(lib, h, findStock, onSimilar) {
    const it = h.it;
    const c = el('article', 'fs-card' + (h.exact ? ' is-exact' : ''));
    const head = el('div', 'fs-card__head');
    const titles = el('div');
    titles.appendChild(el('p', 'fs-card__brand', it.brand));
    titles.appendChild(el('h3', 'fs-card__name', it.name));
    head.appendChild(titles);
    if (h.sim != null) head.appendChild(el('span', 'fs-card__sim', Math.round(h.sim * 100) + '% similar'));
    c.appendChild(head);
    c.appendChild(el('p', 'fs-card__meta', [it.category, it.gender].filter(Boolean).join(' · ')));
    if (it.style || it.bestFor) c.appendChild(el('p', 'fs-card__style', [it.style, it.bestFor].filter(Boolean).join(' — ')));
    if (it.of) c.appendChild(el('p', 'fs-card__dupe', 'Dupe of ' + it.of + (it.from ? ' · from R' + it.from.toLocaleString('en-ZA') : '')));
    if (it.notes) c.appendChild(el('p', 'fs-card__notes', (it.of ? 'Original\u2019s notes: ' : 'Notes: ') + it.notes));
    const bars = el('div', 'fs-bars');
    topDims(lib, it, 3).concat([{ d: 'longevity', v: it.dna[lib.D.indexOf('longevity')] }, { d: 'projection', v: it.dna[lib.D.indexOf('projection')] }]).forEach(({ d, v }) => {
      const row = el('div', 'fs-bar'); row.setAttribute('aria-label', `${cap(d)} ${v} out of 10`);
      row.appendChild(el('span', 'fs-bar__l', cap(d)));
      const tr = el('span', 'fs-bar__t'); const f = el('span', 'fs-bar__f'); f.style.width = v * 10 + '%'; tr.appendChild(f); row.appendChild(tr);
      row.appendChild(el('span', 'fs-bar__n', String(v)));
      bars.appendChild(row);
    });
    c.appendChild(bars);
    const actions = el('div', 'fs-card__actions');
    const sim = el('button', 'fs-link', 'Show similar'); sim.type = 'button';
    sim.addEventListener('click', () => onSimilar(it));
    actions.appendChild(sim);
    if (it.dupes) {
      const dp = el('button', 'fs-link', it.dupes.length + ' SA dupe' + (it.dupes.length === 1 ? '' : 's')); dp.type = 'button';
      dp.addEventListener('click', () => onSimilar(it, 'dupes'));
      actions.appendChild(dp);
    }
    if (it.url) {
      const a = el('a', 'fs-link', 'View at ' + it.brand); a.href = it.url; a.target = '_blank'; a.rel = 'noopener nofollow';
      actions.appendChild(a);
    }
    c.appendChild(actions);
    return c;
  }

  function summary(res, count) {
    const u = res.understood || {};
    if (res.all) return `All ${count.toLocaleString('en-ZA')} fragrances, A–Z by house. Scroll to browse, or type to narrow it down.`;
    if (res.brand) return `${count} ${res.brand} fragrances.`;
    if (res.noteQ) return `${res.total.toLocaleString('en-ZA')} fragrances with ${res.noteQ.join(' and ')} in their notes${res.total > count ? ', top ' + count + ' shown' : ''}.`;
    if (res.dupesOf) return `${res.dupesOf.brand} ${res.dupesOf.name}, and ${count - 1} South African dupe${count === 2 ? '' : 's'} of it, cheapest first.`;
    if (res.similarTo) return `${res.similarTo.brand} ${res.similarTo.name}, and the ${count - 1} fragrances closest to its DNA.`;
    const bits = [];
    const word = { sweetness: 'sweet', freshness: 'fresh', woody: 'woody', spicy: 'spicy', smoky: 'smoky', creamy: 'creamy', citrus: 'citrus', fruity: 'fruity', floral: 'floral', aquatic: 'aquatic', green: 'green', projection: 'strong', longevity: 'long-lasting', warmth: 'warm', versatility: 'versatile', intensity: 'intense' };
    if (u.want && u.want.length) bits.unshift(u.want.slice(0, 4).join(', ').replace('longlasting', 'long-lasting'));
    if (u.avoid && u.avoid.length) bits.push('not ' + u.avoid.join(', '));
    if (u.ctx && u.ctx.length) bits.push('for ' + Array.from(new Set(u.ctx)).join(', '));
    if (u.gender) bits.push(u.gender);
    if (u.cats && u.cats.length) bits.push(u.cats.join(', '));
    return bits.length ? `Matching ${bits.join(' · ')}: ${count} fragrances.` : `${count} fragrances found.`;
  }

  function render(box, statusEl, lib, query, findStock) {
    const res = search(lib, query);
    box.innerHTML = '';
    if (!norm(query)) { statusEl.textContent = ''; return res; }
    if (!res.hits.length) {
      statusEl.textContent = `Nothing matched “${query}”. Try a brand, a note like vanilla or oud, or a mood like fresh for summer.`;
      return res;
    }
    statusEl.textContent = summary(res, res.hits.length);
    const onSimilar = (it, mode) => {
      const input = box.closest('[data-fs]').querySelector('.js-fs-input');
      input.value = mode === 'dupes' ? it.brand + ' ' + it.name + ' dupes' : 'smells like ' + it.brand + ' ' + it.name;
      input.dispatchEvent(new Event('input'));
      const sc = box.closest('[data-fs]').querySelector('.fs__scroll');
      if (sc) sc.scrollTo({ top: 0, behavior: 'smooth' }); else input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    if (box._fsObs) { box._fsObs.disconnect(); box._fsObs = null; }
    if (!res.all) { res.hits.forEach((h) => box.appendChild(card(lib, h, findStock, onSimilar))); return res; }
    // All: draw in batches as the customer scrolls so thousands of cards stay smooth
    let i = 0; let lastBrand = '';
    const sentinel = el('div', 'fs__more');
    const batch = () => {
      const end = Math.min(i + 48, res.hits.length);
      for (; i < end; i++) {
        const h = res.hits[i];
        if (h.it.brand !== lastBrand) { lastBrand = h.it.brand; box.insertBefore(el('h3', 'fs__house', lastBrand), sentinel); }
        box.insertBefore(card(lib, h, findStock, onSimilar), sentinel);
      }
      if (i >= res.hits.length && box._fsObs) { box._fsObs.disconnect(); sentinel.remove(); }
    };
    box.appendChild(sentinel);
    batch();
    if (i < res.hits.length && 'IntersectionObserver' in window) {
      box._fsObs = new IntersectionObserver((en) => { if (en.some((e) => e.isIntersecting)) batch(); }, { root: box.closest('.fs__scroll'), rootMargin: '800px 0px' });
      box._fsObs.observe(sentinel);
    } else { while (i < res.hits.length) batch(); }
    return res;
  }

  /* ---------- wire up: overlay and search page ---------- */
  function mount(root) {
    const input = $('.js-fs-input', root), box = $('.js-fs-results', root), status = $('.js-fs-status', root);
    let lib = null, findStock = null, t;
    const intro = $('.fs__intro', root);
    const run = () => {
      if (intro && root.matches('.fs-modal, .fs-modal *, [data-fs]:not([data-fs-page])')) {
        intro.hidden = !!input.value.trim();
        const hl = $('.fs__headline', root); if (hl) hl.hidden = intro.hidden;
      }
      if (lib) render(box, status, lib, input.value, findStock);
    };
    if (!input) return Promise.resolve();
    const ready = loadLibrary().then((l) => { lib = l; root.querySelectorAll('.js-fs-count, .js-fs-count-all').forEach((c) => { c.textContent = l.items.length.toLocaleString('en-ZA'); }); run(); });
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(run, 120); });
    if (root.matches('[data-fs-page]') && input.type !== 'hidden') {
      const q = new URLSearchParams(location.search).get('q');
      if (q) input.value = q;
    }
    root.querySelectorAll('.js-fs-chip').forEach((b) => b.addEventListener('click', () => { input.value = b.dataset.q; run(); input.focus(); }));
    return ready;
  }

  let overlay = null;
  function openSearch(e) {
    const dlg = document.querySelector('.js-fs-modal');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    if (e) e.preventDefault();
    if (!overlay) overlay = mount(dlg);
    const drawer = document.querySelector('.js-drawer');
    if (drawer && drawer.open) { drawer.open = false; document.body.style.overflow = ''; }
    dlg.showModal();
    const inp = $('.js-fs-input', dlg); if (inp) inp.focus();
    // links like "Explore woody scents" open the search already filled in
    const q = e && e.currentTarget && e.currentTarget.dataset ? e.currentTarget.dataset.q : '';
    if (q && inp) { inp.value = q; inp.dispatchEvent(new Event('input')); }
  }
  function closeSearch() { const dlg = document.querySelector('.js-fs-modal'); if (dlg && dlg.open) dlg.close(); }

  document.querySelectorAll('.js-open-search').forEach((a) => a.addEventListener('click', openSearch));
  const dlg = document.querySelector('.js-fs-modal');
  if (dlg) {
    $('.js-fs-close', dlg).addEventListener('click', closeSearch);
    dlg.addEventListener('click', (e) => { if (e.target === dlg) closeSearch(); });
    const form = $('.js-fs-form', dlg); if (form) form.addEventListener('submit', (e) => e.preventDefault());
    dlg.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); closeSearch(); } });
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && !/input|textarea|select/i.test(document.activeElement.tagName) && !document.querySelector('dialog[open]')) openSearch(e);
  });
  document.querySelectorAll('[data-fs-page]').forEach((root) => mount(root));

  window.FifthSearch = { search: (q) => loadLibrary().then((l) => search(l, q)), open: openSearch };
})();
